import React, { useEffect, useState } from "react"
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  LockKeyhole,
} from "lucide-react"
import { TiptapEditor } from "../../../../component/tiptapEditor"
import { hasAnswerContent } from "../../PracticePapers/utils/hasAnswerContent"
import type { Attempt } from "../types"
import { attemptDropdownLabel } from "../utils/attemptStatus"
import AttemptHistoryTable from "./AttemptHistoryTable"
import LockedAttemptBanner from "./LockedAttemptBanner"
import AIFeedbackPanel from "./AIFeedbackPanel"
import "./AttemptWorkspace.style.scss"

interface AttemptWorkspaceProps {
  attempts: Attempt[]
  maxAttempts: number
  canStartNew: boolean
  hasMarkingScheme: boolean
  canAiMark?: boolean
  onStart: () => void
  onUpdateDraft: (attemptId: string, text: string) => void
  onSubmit: (attemptId: string) => void
}

const AttemptWorkspace = ({
  attempts,
  maxAttempts: _maxAttempts,
  canStartNew,
  hasMarkingScheme,
  canAiMark,
  onStart,
  onUpdateDraft,
  onSubmit,
}: AttemptWorkspaceProps) => {
  const [viewMode, setViewMode] = useState<"active" | "history">("active")
  const [currentAttemptId, setCurrentAttemptId] = useState<string | null>(null)

  const latest = attempts.length > 0 ? attempts[attempts.length - 1] : null

  useEffect(() => {
    if (!attempts.length) {
      setCurrentAttemptId(null)
      return
    }
    setCurrentAttemptId((prev) =>
      prev && attempts.some((a) => a.id === prev) ? prev : latest!.id
    )
  }, [attempts, latest])

  const activeAttempt =
    attempts.find((a) => a.id === currentAttemptId) ?? latest
  const isViewingLatest =
    !currentAttemptId || currentAttemptId === latest?.id
  const isDraft = activeAttempt?.status === "draft"
  const isLocked = !!activeAttempt && !isDraft
  const allowNew = canStartNew && isViewingLatest
  const aiMark = canAiMark ?? hasMarkingScheme

  if (!attempts.length) {
    return (
      <div className="attempt-workspace attempt-workspace--empty">
        <div className="attempt-workspace__empty-icon">
          <FileText size={24} />
        </div>
        <h4>No attempts yet</h4>
        <p>
          Write your answer below, submit it, and the AI Assessor will score it
          against the marking scheme.
        </p>
        <button type="button" className="is-primary" onClick={onStart}>
          Start Attempt
        </button>
      </div>
    )
  }

  if (viewMode === "history") {
    return (
      <AttemptHistoryTable
        attempts={attempts}
        canStartNew={allowNew}
        onNewAttempt={() => {
          onStart()
          setViewMode("active")
        }}
        onSelect={(id) => {
          setCurrentAttemptId(id)
          setViewMode("active")
        }}
        onBack={() => setViewMode("active")}
      />
    )
  }

  return (
    <div className="attempt-workspace">
      <div className="attempt-workspace__selector">
        <select
          value={activeAttempt?.id || ""}
          onChange={(e) => setCurrentAttemptId(e.target.value)}
          aria-label="Select attempt"
        >
          {attempts.map((a) => (
            <option key={a.id} value={a.id}>
              {attemptDropdownLabel(a)}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="attempt-workspace__history-btn"
          onClick={() => setViewMode("history")}
        >
          History ({attempts.length})
        </button>
      </div>

      {isDraft && !aiMark && (
        <div className="attempt-workspace__notice is-warn">
          <AlertTriangle size={16} />
          <div>
            <p className="is-title">No Marking Scheme — AI Assessment Unavailable</p>
            <p>
              No official marking scheme exists for this question. After
              submitting, compare your work with the <strong>Teacher Solution</strong>{" "}
              tab.
            </p>
          </div>
        </div>
      )}

      {isLocked && activeAttempt && (
        <LockedAttemptBanner
          attempt={activeAttempt}
          canStartNew={allowNew}
          onNewAttempt={onStart}
        />
      )}

      <div
        className={`attempt-workspace__answer${isLocked ? " is-locked" : ""}`}
      >
        {activeAttempt && (
          <TiptapEditor
            key={activeAttempt.id}
            id={`attempt-answer-${activeAttempt.id}`}
            value={activeAttempt.answerText}
            height={220}
            editable={isDraft}
            placeholder="Write your answer here. Your progress is saved automatically."
            handleOnChange={(html) => onUpdateDraft(activeAttempt.id, html)}
          />
        )}
        {isLocked && (
          <span className="attempt-workspace__readonly">
            <LockKeyhole size={12} /> Read only
          </span>
        )}
      </div>

      {isDraft && activeAttempt && (
        <div className="attempt-workspace__submit-row">
          <p>Attempt #{activeAttempt.attemptNumber}</p>
          <button
            type="button"
            className="is-primary"
            disabled={!hasAnswerContent(activeAttempt.answerText)}
            onClick={() => onSubmit(activeAttempt.id)}
          >
            <CheckCircle2 size={16} />
            {aiMark ? "Submit for AI Marking" : "Mark as Completed"}
          </button>
        </div>
      )}

      {activeAttempt?.status === "marking" && (
        <div className="attempt-workspace__marking">
          <div className="attempt-workspace__spinner" />
          <div>
            <h4>AI Assessor is marking…</h4>
            <p>Scoring your answer against the marking scheme.</p>
          </div>
        </div>
      )}

      {activeAttempt?.status === "submitted" && (
        <div className="attempt-workspace__completed">
          <div className="attempt-workspace__completed-head">
            <span>
              <CheckCircle2 size={20} />
            </span>
            <div>
              <h4>Attempt Completed</h4>
              <p>
                Submitted{" "}
                {activeAttempt.submittedAt
                  ? new Date(activeAttempt.submittedAt).toLocaleString("en-IE", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : ""}
              </p>
            </div>
          </div>
          <div className="attempt-workspace__notice is-warn">
            <AlertTriangle size={18} />
            <div>
              <p className="is-title">AI Assessment Not Available</p>
              <p>
                No official marking scheme is linked to this question. Compare
                your answer with the Teacher Solution tab.
              </p>
            </div>
          </div>
          {allowNew && (
            <div className="attempt-workspace__completed-actions">
              <button type="button" className="is-primary" onClick={onStart}>
                New Attempt
              </button>
            </div>
          )}
        </div>
      )}

      {activeAttempt?.status === "failed" && (
        <div className="attempt-workspace__completed">
          <div className="attempt-workspace__notice is-warn">
            <AlertTriangle size={18} />
            <div>
              <p className="is-title">AI marking failed</p>
              <p>
                {activeAttempt.assessmentError ||
                  "The assessor could not mark this attempt. You can try again."}
              </p>
            </div>
          </div>
        </div>
      )}

      {(activeAttempt?.status === "marked" ||
        activeAttempt?.status === "flagged_for_review") && (
        <AIFeedbackPanel
          attempt={activeAttempt}
          canStartNew={allowNew}
          onNewAttempt={onStart}
        />
      )}
    </div>
  )
}

export default AttemptWorkspace
